import { Fragment } from "react";
import { Time } from "../../config/Constants";
import Compress from 'compress.js';
import { DateTime } from "luxon";
import { BillingCycle } from "./Models";

/**
 * Can use it in catch block for axios requests
 * @param e
 */
export function parseErrorForAxios(e: any): string {
  return (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
}

/**
 * Compute the end of a billing cycle from its start - mirrors backend getBillingCycleEnd in OrderCalculation.ts
 * @param cycleStart
 * @param billingCycle
 */
export function computeBillingCycleEnd(cycleStart: number, billingCycle: BillingCycle): number {
  const start = DateTime.fromMillis(cycleStart);
  switch (billingCycle) {
    case BillingCycle.DAILY: return start.plus({ days: 1 }).toMillis();
    case BillingCycle.WEEKLY: return start.plus({ weeks: 1 }).toMillis();
    case BillingCycle.FIFTEEN_DAYS: return start.plus({ days: 15 }).toMillis();
    case BillingCycle.MONTHLY: return start.plus({ months: 1 }).toMillis();
    case BillingCycle.QUARTERLY: return start.plus({ months: 3 }).toMillis();
  }
}

/**
 * Human-readable label for a billing cycle - not derivable from the enum string alone (FIFTEEN_DAYS isn't a single word)
 */
export const billingCycleDisplayLabel: Record<BillingCycle, string> = {
  [BillingCycle.DAILY]: 'Daily',
  [BillingCycle.WEEKLY]: 'Weekly',
  [BillingCycle.FIFTEEN_DAYS]: '15 Days',
  [BillingCycle.MONTHLY]: 'Monthly',
  [BillingCycle.QUARTERLY]: 'Quarterly'
};


/**
 * Converts input type file selection to base64 string
 * @param file File
 */
export function convertBase64(file: Blob) {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file)
    fileReader.onload = () => {
      resolve(fileReader.result);
    }
    fileReader.onerror = (error) => {
      reject(error);
    }
  })
}


/**
 * Function to resize image file
 * @param file 
 * @returns 
 */
export async function resizeImageFn(file: any) {
  const compress = new Compress();
  const resizedImage = await compress.compress([file], {
    size: 2, // the max size in MB, defaults to 2MB
    quality: 0.8, // the quality of the image, max is 1,
    // maxWidth: 300, // the max width of the output image, defaults to 1920px
    // maxHeight: 300, // the max height of the output image, defaults to 1920px
    resize: true // defaults to true, set false if you do not want to resize the image width and height
  })
  const img = resizedImage[0];
  const base64str = img.data
  const imgExt = img.ext
  const resizedFile = Compress.convertBase64ToFile(base64str, imgExt)
  return resizedFile;
}


/**
 * Limit number to k decimal places
 * @param n
 * @param decimalPlaces
 */
export function toFixed(n: number, decimalPlaces: number) {
  return Number(n.toFixed(decimalPlaces));
}

/**
 * Limit number to 2 decimal places
 * @param n
 */
export function toFixed2(n: number) {
  return toFixed(n, 2);
}

/**
 * Round and limit number to 2 decimal places
 * @param n
 */
export function roundAndToFixed2(n: number) {
  return toFixed2(Math.round((n + Number.EPSILON) * 100) / 100);
}


/**
 * Utility to convert ts to IST date string
 * @param ts 
 * @returns 
 */
export function toIstDateString(ts: number, options?: { removeDay?: boolean, returnJsx?: boolean }) {
  const initDate = new Date(Number(ts));
  const dateTime = initDate.getTime();
  const dateTimeUtc = dateTime + initDate.getTimezoneOffset() * 60000;
  const date = new Date(dateTimeUtc + 5.5 * 3600000);
  const hours = date.getHours();
  const hours12 = (hours === 12) ? (12) : (hours % 12);
  const hoursAmPm = (hours < 12) ? ('AM') : ('PM');
  let minute = date.getMinutes().toString();
  if (date.getMinutes() === 0)
    minute = '00';
  else if (date.getMinutes() < 10)
    minute = '0' + minute;
  const resultTime = hours12 + ':' + minute + ' ' + hoursAmPm;
  const resultDate = (options?.removeDay ? (date).toDateString().replace(/^\S+\s/, '') : (date).toDateString());
  if (options?.returnJsx) {
    return (
      <Fragment>
        <div className="fs-14">{resultDate}</div>
        <div className="fs-14">{resultTime}</div>
      </Fragment>
    )
  }
  return resultTime + ' ' + resultDate;
}

/**
 * Get human readable string in hours and days from millis
 * @param n
 */
export function millisToHumanReadableDuration(n: number): string {
  const moreThanDay = n > Time.DAY;
  let dayCount = Math.floor(n / Time.DAY);
  const extraMillis = moreThanDay ? n - (dayCount * Time.DAY) : 0;
  let hourCount = moreThanDay ? Math.round(extraMillis / Time.HOUR) : Math.round(n / Time.HOUR);
  if (hourCount === 24) {
    hourCount = 0;
    dayCount++;
  }
  const dayString = dayCount ? `${dayCount} day${dayCount > 1 ? 's' : ''}` : '';
  const hourString = hourCount ? `${hourCount} hour${hourCount > 1 ? 's' : ''}` : '';
  return `${dayString}${dayString ? ' ' : ''}${hourString}`;
}

/**
 * Capitalize first letter
 * @param s
 * @returns 
 */
export function capitalizeFirstLetter(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}